import { html, LitElement } from 'lit';
import { customElement, property} from 'lit/decorators.js';
import type { ElementInternals } from "element-internals-polyfill/dist/element-internals";

/**
 * Component for displaying a date and/or time in a format appropriate for a user's browser locale.
 */
@customElement('local-time')
export class LocalTime extends LitElement implements HTMLTimeElement {

    /**
     * The date and time to display, in a format accepted by the JavaScript Date constructor
     * (e.g. "2024-01-01T12:00:00Z" for January 1st, 2024 at 12:00 UTC).
     */
    @property({attribute: 'datetime'})
    dateTime = '';

    /**
     * The format in which to display the date and time.
     */
    @property()
    format: 'date' | 'datetime' | 'datetime-seconds' | 'datetime-tz' | 'time' | 'time-tz' = 'datetime';

    private internals: ElementInternals;
    private static formatterOptions: Record<typeof LocalTime.prototype.format, Intl.DateTimeFormatOptions> = {
        date: {
            dateStyle: 'short'
        },
        datetime: {
            dateStyle: 'short',
            timeStyle: 'short',
        },
        "datetime-seconds": {
            dateStyle: 'short',
            timeStyle: 'medium',
        },
        "datetime-tz": {
            dateStyle: 'short',
            timeStyle: 'long',
        },
        time: {
            timeStyle: 'medium',
        },
        "time-tz": {
            timeStyle: 'long',
        }
    };

    constructor() {
        super();
        this.internals = this.attachInternals();
        this.internals.role = 'time';
    }

    override render() {
        let localizedTime: string;
        try {
            localizedTime = LocalTime.formatDate(this.dateTime, this.format);
        } catch (e) {
            localizedTime = '!!!ERR!!!';
            console.error(e);
        }
        return html`
            <span>${localizedTime}</span>
        `;
    }


    public static formatDate(dateTime: string, format: typeof LocalTime.prototype.format): string {
        const date = new Date(dateTime);
        const formatOptions = LocalTime.formatterOptions[format];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!formatOptions) {
            throw new TypeError(`unknown format value ${format}`);
        }
        return Intl.DateTimeFormat(undefined, formatOptions).format(date);
    }
}
